import React, { useCallback, useMemo, useState } from "react";
import { Area, CartesianGrid, ComposedChart, XAxis, YAxis } from "recharts";
import dayjs from "dayjs";
import { ChevronDown, BarChart2, Table2, Settings, Search } from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";
import TableComponent from "@components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tracify/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@tracify/ui/popover";
import { SidebarBadge } from "@tracify/ui/sidebar-badge";
import { Checkbox } from "@tracify/ui/checkbox";
import { Input } from "@tracify/ui/input";
import { Separator } from "@tracify/ui/separator";
import { Card, CardContent } from "@tracify/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
} from "@tracify/ui/chart";
import {
  useDecifyChannelOverview,
  getChannelColor,
  formatChannelLabel,
  isTracifySource,
  type ChannelSeries,
  type ChannelMetric,
} from "@lib/api-hooks/useDecify/useDecifyChannelOverview";
import {
  useDecifyCredentials,
  useDecifyProjects,
  useDecifyMappings,
} from "@lib/api-hooks/useDecify/useDecify";
import { Tooltip, TooltipContent, TooltipTrigger } from "@tracify/ui/tooltip";
import InfoIcon from "@tracify/ui/info-icon";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import useCurrency from "@lib/hooks/use-currency";
import useFormat from "@lib/hooks/use-format";
import { useDateRangeState } from "@lib/hooks/url-state";
import DecifyIconDark from "@components/Layout/icons/decify-icon-dark";
import { Button } from "@tracify/ui/button";
import LinkWithPersistentQuery from "@components/LinkWithQuery/LinkWithQuery";
import { getMetricLabelForDashboardMode } from "@/constants/constants";
import { dashboardModeAtom } from "@/atoms";
import { useAtomValue } from "jotai";

const EMPTY_CHANNEL_SERIES: ChannelSeries[] = [];

function SurveyTooltip({
  active,
  payload,
  label,
  formatValue,
}: {
  active?: boolean;
  payload?: readonly any[];
  label?: string | number;
  formatValue: (v: number) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background border border-border/50 rounded-lg px-3 py-2 text-xs shadow-xl min-w-[170px]">
      <p className="font-medium mb-2 text-foreground">
        {dayjs(String(label)).format("YYYY-MM-DD")}
      </p>
      {[...payload]
        .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
        .map((entry: any) => (
          <div
            key={entry.dataKey}
            className="flex items-center gap-2 mb-1 last:mb-0"
          >
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: entry.color }}
            />
            <span className="text-muted-foreground">{entry.name}:</span>
            <span className="text-foreground font-medium ml-auto pl-2">
              {formatValue(entry.value)}
            </span>
          </div>
        ))}
    </div>
  );
}

export function PpsSurveyChannelOverviewChart() {
  const { selectedStoreIds } = useSelectedStores();
  const csid = selectedStoreIds[0];
  const { data: creds, isError, isLoading } = useDecifyCredentials(csid);
  const { data: projects = [] } = useDecifyProjects(creds ? csid : "");
  const firstProjectId = projects[0]?.id ?? null;
  const { data: mappings = [] } = useDecifyMappings(
    firstProjectId,
    creds ? csid : ""
  );
  const { startTime, endTime } = useDateRangeState();
  const [metric, setMetric] = useState<ChannelMetric>("revenue");
  const [hiddenChannels, setHiddenChannels] = useState<Set<string>>(new Set());
  const [channelSearch, setChannelSearch] = useState("");
  const [view, setView] = useState<"chart" | "table">("chart");
  const dashboardMode = useAtomValue(dashboardModeAtom);

  function toggleChannel(channel: string) {
    setHiddenChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) {
        next.delete(channel);
      } else {
        next.add(channel);
      }
      return next;
    });
  }

  const { data, isLoading: isDataLoading } = useDecifyChannelOverview(
    creds ? csid : undefined,
    startTime,
    endTime
  );

  const { calculateCurrencyConvertion } = useCurrency();
  const { formatNumber } = useFormat();
  const metricLabels = useMemo(
    () => ({
      revenue: getMetricLabelForDashboardMode(
        "customer_revenue",
        dashboardMode
      ),
      orders: getMetricLabelForDashboardMode(
        "customer_purchase",
        dashboardMode
      ),
      aov: getMetricLabelForDashboardMode("average_order_value", dashboardMode),
    }),
    [dashboardMode]
  );
  const series = data?.series ?? EMPTY_CHANNEL_SERIES;
  const channels = useMemo(
    () =>
      [...series.map((s) => s.channel)].sort(
        (a, b) => (isTracifySource(a) ? 0 : 1) - (isTracifySource(b) ? 0 : 1)
      ),
    [series]
  );
  const channelLabels = useMemo(
    () =>
      Object.fromEntries(
        series.map((s) => [s.channel, formatChannelLabel(s.channel)])
      ) as Record<string, string>,
    [series]
  );

  const channelMeta = useMemo(
    () =>
      Object.fromEntries(
        channels.map((c, i) => [
          c,
          { color: getChannelColor(c, i), gradientId: `pps-gradient-${i}` },
        ])
      ) as Record<string, { color: string; gradientId: string }>,
    [channels]
  );

  const visibleChannels = useMemo(
    () => channels.filter((c) => !hiddenChannels.has(c)),
    [channels, hiddenChannels]
  );

  const filteredChannels = useMemo(() => {
    const normalizedSearch = channelSearch.trim().toLocaleLowerCase();
    if (!normalizedSearch) return channels;

    return channels.filter((channel) =>
      (channelLabels[channel] ?? channel)
        .toLocaleLowerCase()
        .includes(normalizedSearch)
    );
  }, [channelLabels, channelSearch, channels]);

  const allChannelsSelected =
    channels.length > 0 && visibleChannels.length === channels.length;

  const toggleAllChannels = useCallback(() => {
    setHiddenChannels(allChannelsSelected ? new Set(channels) : new Set());
  }, [allChannelsSelected, channels]);

  const chartConfig = useMemo<ChartConfig>(
    () =>
      Object.fromEntries(
        channels.map((c) => [
          c,
          { label: channelLabels[c] ?? c, color: channelMeta[c]?.color },
        ])
      ),
    [channels, channelMeta, channelLabels]
  );

  const chartData = useMemo(() => {
    if (!series.length) return [];
    const field =
      metric === "orders"
        ? "orders"
        : metric === "aov"
          ? "average_order_value"
          : "revenue";
    return series[0].data.map(({ date }, i) => {
      const point: Record<string, string | number> = { date };
      for (const channelSeries of series) {
        point[channelSeries.channel] = channelSeries.data[i]?.[field] ?? 0;
      }
      return point;
    });
  }, [metric, series]);

  const formatValue = useCallback(
    (value: number) =>
      metric === "orders"
        ? formatNumber(value, 0)
        : calculateCurrencyConvertion(value, 0, true),
    [metric, formatNumber, calculateCurrencyConvertion]
  );

  const tableData = useMemo(() => {
    if (!series.length) return [];
    return [...series]
      .map((s) => {
        const totalRevenue = s.data.reduce((sum, d) => sum + d.revenue, 0);
        const totalOrders = s.data.reduce((sum, d) => sum + d.orders, 0);
        return {
          channel: s.channel,
          label: formatChannelLabel(s.channel),
          revenue: totalRevenue,
          orders: totalOrders,
          aov: totalOrders > 0 ? totalRevenue / totalOrders : 0,
        };
      })
      .sort((a, b) => b.revenue - a.revenue);
  }, [series]);

  const tableTotals = useMemo(() => {
    const totalRevenue = tableData.reduce((sum, r) => sum + r.revenue, 0);
    const totalOrders = tableData.reduce((sum, r) => sum + r.orders, 0);
    return {
      revenue: totalRevenue,
      orders: totalOrders,
      aov: totalOrders > 0 ? totalRevenue / totalOrders : 0,
    };
  }, [tableData]);

  const tableColumns = useMemo<ColumnDef<(typeof tableData)[number]>[]>(
    () => [
      {
        id: "channel",
        header: "Channel",
        accessorKey: "label",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ background: channelMeta[row.original.channel]?.color }}
            />
            <span className="text-sm font-medium">{row.original.label}</span>
          </div>
        ),
        footer: () => <span className="text-sm font-semibold">Total</span>,
      },
      {
        id: "revenue",
        header: metricLabels.revenue,
        accessorKey: "revenue",
        cell: ({ getValue }) => (
          <span className="text-sm">
            {calculateCurrencyConvertion(getValue() as number, 0, true)}
          </span>
        ),
        footer: () => (
          <span className="text-sm font-semibold">
            {calculateCurrencyConvertion(tableTotals.revenue, 0, true)}
          </span>
        ),
      },
      {
        id: "orders",
        header: metricLabels.orders,
        accessorKey: "orders",
        cell: ({ getValue }) => (
          <span className="text-sm">
            {formatNumber(getValue() as number, 0)}
          </span>
        ),
        footer: () => (
          <span className="text-sm font-semibold">
            {formatNumber(tableTotals.orders, 0)}
          </span>
        ),
      },
      {
        id: "aov",
        header: metricLabels.aov,
        accessorKey: "aov",
        cell: ({ getValue }) => (
          <span className="text-sm">
            {calculateCurrencyConvertion(getValue() as number, 0, true)}
          </span>
        ),
        footer: () => (
          <span className="text-sm font-semibold">
            {calculateCurrencyConvertion(tableTotals.aov, 0, true)}
          </span>
        ),
      },
    ],
    [
      channelMeta,
      calculateCurrencyConvertion,
      formatNumber,
      metricLabels,
      tableTotals,
    ]
  );

  if (isLoading) return null;

  if (isError || !creds) {
    return (
      <Card className="p-4 lg:p-6">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center text-center py-16">
            <div className="max-w-lg mx-auto flex flex-col items-center">
              <DecifyIconDark />
              <p className="mt-8">
                Import your post purchase survey data to Tracify and get more
                insight into your paid and organic channels.
              </p>
              <div className="flex gap-3 mt-8">
                <LinkWithPersistentQuery href="/settings/integrations/decify">
                  <Button variant="primary" className="min-w-40">
                    Connect to Decify
                  </Button>
                </LinkWithPersistentQuery>
                <a
                  href="https://admin.decify.co/signin"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="secondary" className="min-w-40">
                    Sign up for Decify
                  </Button>
                </a>
              </div>
            </div>
            <p className="mt-6 text-foreground-soft text-sm w-full">
              Decify is an add-on in cooperation with Tracify. Decify survey
              data is not included in Tracify&apos;s attribution data.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative z-20 p-4 lg:p-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="h3">Post Purchase Survey Overview</h3>
            <SidebarBadge variant="warning">Beta</SidebarBadge>
            <Tooltip>
              <TooltipTrigger className="flex items-center mt-0.5">
                <InfoIcon />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                Survey responses are mapped to Tracify channels (or custom
                channels) based on your configuration. Compare self-reported
                attribution against your performance data.
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <LinkWithPersistentQuery
                  href="/settings/integrations/decify"
                  className="flex items-center mt-0.5 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Settings className="w-4 h-4" />
                </LinkWithPersistentQuery>
              </TooltipTrigger>
              <TooltipContent>Manage channel mappings</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Channel performance breakdown by post-purchase survey responses
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {view === "chart" && (
            <Select
              value={metric}
              onValueChange={(v) => setMetric(v as ChannelMetric)}
            >
              <SelectTrigger
                variant="alternate"
                className="w-full sm:w-[140px] font-semibold"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="revenue">{metricLabels.revenue}</SelectItem>
                <SelectItem value="orders">{metricLabels.orders}</SelectItem>
                <SelectItem value="aov">{metricLabels.aov}</SelectItem>
              </SelectContent>
            </Select>
          )}
          {channels.length > 0 && (
            <Popover
              onOpenChange={(open) => {
                if (!open) setChannelSearch("");
              }}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Select PPS channels"
                  className="flex h-10 w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-input bg-background pl-3 pr-2 text-sm font-semibold shadow-sm transition-all focus:outline-none focus:ring-1 focus:ring-ring sm:min-w-[180px] lg:h-12"
                >
                  {allChannelsSelected
                    ? "All Channels"
                    : `${visibleChannels.length} / ${channels.length} Channels`}
                  <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                collisionPadding={8}
                data-testid="pps-channel-popover"
                className="flex max-h-[min(24rem,var(--radix-popover-content-available-height))] w-80 max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-0"
              >
                <div className="flex shrink-0 items-center gap-2 p-2">
                  <Input
                    type="search"
                    value={channelSearch}
                    onChange={(event) => setChannelSearch(event.target.value)}
                    placeholder="Search channels..."
                    aria-label="Search channels"
                    size="sm"
                    variant="alternate"
                    startAdornment={
                      <Search
                        aria-hidden="true"
                        className="size-4 text-muted-foreground"
                      />
                    }
                    className="min-w-0 flex-1 px-2"
                  />
                  <button
                    type="button"
                    onClick={toggleAllChannels}
                    aria-label={
                      allChannelsSelected
                        ? "Unselect all channels"
                        : "Select all channels"
                    }
                    className="w-20 shrink-0 whitespace-nowrap rounded-sm px-1.5 py-1 text-xs font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {allChannelsSelected ? "Unselect all" : "Select all"}
                  </button>
                  <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {visibleChannels.length}/{channels.length}
                  </span>
                </div>
                <Separator />
                <div
                  data-testid="pps-channel-list"
                  className="min-h-0 flex-1 overflow-y-auto p-2"
                >
                  {filteredChannels.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      {filteredChannels.map((channel) => (
                        <label
                          key={channel}
                          className="flex cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                        >
                          <Checkbox
                            checked={!hiddenChannels.has(channel)}
                            onCheckedChange={() => toggleChannel(channel)}
                          />
                          <span
                            className="size-2.5 shrink-0 rounded-sm"
                            style={{ background: channelMeta[channel]?.color }}
                          />
                          <span className="truncate">
                            {channelLabels[channel] ?? channel}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                      No channels found.
                    </p>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
          <div className="flex items-center rounded-lg border border-input bg-background h-10 lg:h-12 p-1">
            <button
              onClick={() => setView("chart")}
              className={`flex items-center justify-center px-2.5 h-full rounded-md transition-colors ${
                view === "chart"
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              title="Chart view"
            >
              <BarChart2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setView("table")}
              className={`flex items-center justify-center px-2.5 h-full rounded-md transition-colors ${
                view === "table"
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              title="Table view"
            >
              <Table2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {!isDataLoading && !series.length ? (
        <div className="h-[300px] flex items-center justify-center text-sm text-muted-foreground">
          No survey data for the selected period.
        </div>
      ) : view === "table" ? (
        <TableComponent
          data={tableData}
          columns={tableColumns}
          hidePagination
          showFooter
          spacing="tight"
          sorting={[{ id: "revenue", desc: true }]}
        />
      ) : (
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
          >
            <defs>
              {visibleChannels.map((channel) => (
                <linearGradient
                  key={channel}
                  id={channelMeta[channel].gradientId}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={channelMeta[channel].color}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={channelMeta[channel].color}
                    stopOpacity={0}
                  />
                </linearGradient>
              ))}
            </defs>
            {visibleChannels.map((channel) => (
              <Area
                key={channel}
                isAnimationActive={false}
                type="monotone"
                dataKey={channel}
                name={channelLabels[channel] ?? channel}
                stroke={channelMeta[channel].color}
                strokeWidth={2}
                fill={`url(#${channelMeta[channel].gradientId})`}
                fillOpacity={1}
                dot={false}
                connectNulls
                animationDuration={600}
              />
            ))}
            <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              tickFormatter={(d) => dayjs(d).format("DD/MM")}
              interval="preserveStartEnd"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              tickFormatter={formatValue}
              width={70}
            />
            <ChartTooltip
              content={(props) => (
                <SurveyTooltip
                  active={props.active}
                  payload={props.payload}
                  label={props.label}
                  formatValue={formatValue}
                />
              )}
            />
            <ChartLegend
              content={<ChartLegendContent className="flex-wrap px-4" />}
            />
          </ComposedChart>
        </ChartContainer>
      )}
    </Card>
  );
}
